/**
 * @fileoverview Helpers for TaskForm submit: tag name maps and catalog ensure.
 */
import { ensureTermsExist } from '@/services/tagService';
import { buildNameMapForTerms } from '@/services/tagNameUtils';
import { normalizeTaskTags, normalizeTagId } from '@/features/tasks/utils/ui/normalizeTaskTags';

/**
 * Build id→display-name map from raw tag strings, then overlay catalog names.
 * @param {unknown[]} rawTerms
 * @param {string[]} normalizedIds
 * @param {Map<string, string>} displayMap
 * @returns {Record<string, string>}
 */
export function buildTagNameMap(rawTerms, normalizedIds, displayMap) {
  /** @type {Record<string, string>} */
  const names = {};
  for (const raw of rawTerms || []) {
    const id = normalizeTagId(raw);
    if (id && !(id in names)) {
      names[id] =
        String(raw)
          .replace(/^[@#]/, '')
          .trim() || id;
    }
  }
  for (const id of normalizedIds || []) {
    const catalog = displayMap.get(id);
    if (catalog) names[id] = catalog;
  }
  return names;
}

/**
 * Normalize tags, ensure catalog terms exist, then call handleSubmit.
 * @param {{
 *   taskPayload: UpdateTask | NewTask,
 *   user: { uid: string } | null | undefined,
 *   guardProjects: (projects: string[]) => boolean,
 *   projectDisplayMap: Map<string, string>,
 *   contextDisplayMap: Map<string, string>,
 *   isNewTask: boolean,
 *   pendingSubtasks: unknown[],
 *   handleSubmit: (payload: UpdateTask | NewTask) => Promise<void>,
 * }} args
 */
export async function submitTaskFormWithTags({
  taskPayload,
  user,
  guardProjects,
  projectDisplayMap,
  contextDisplayMap,
  isNewTask,
  pendingSubtasks,
  handleSubmit,
}) {
  const normalized = normalizeTaskTags(taskPayload);
  const projectNames = buildTagNameMap(
    taskPayload.projects || [],
    normalized.projects || [],
    projectDisplayMap,
  );
  const contextNames = buildTagNameMap(
    taskPayload.contexts || [],
    normalized.contexts || [],
    contextDisplayMap,
  );

  if (user?.uid) {
    if (normalized.projects?.length && !guardProjects(normalized.projects)) {
      return;
    }
    await ensureTermsExist(
      normalized.projects,
      'projects',
      user.uid,
      buildNameMapForTerms(normalized.projects, projectNames),
    );
    await ensureTermsExist(
      normalized.contexts,
      'contexts',
      user.uid,
      buildNameMapForTerms(normalized.contexts, contextNames),
    );
  }

  if (!isNewTask) return handleSubmit(normalized);
  return handleSubmit({ ...normalized, pendingSubtasks });
}
